import { Link } from 'react-router-dom';

import { formatRelativeDate, getLeadStageStyle, type Lead } from '../../../features/leads/index.js';

import { LeadStageBadge } from './LeadStageBadge.js';

/**
 * Lista de cards de lead para viewport mobile (`< sm`).
 *
 * **Layout** (decisão Alexandre 2026-04-25 — feedback "não esto achando
 * legal estes cards"):
 *
 *   ┌─────────────────────────────────┐
 *   │ ┃ [✍ ASSINADO]                  │  ← badge no topo (eyebrow)
 *   │ ┃ Dra. Daniela Cristina Magno   │  ← nome em destaque (line-clamp-2)
 *   │ ┃ Divino Ser                    │  ← clínica
 *   │ ┃ daniela.magno@gmail.com       │  ← email
 *   │ ┃ Há 7 dias                     │  ← relativo curto (não absoluto)
 *   └─────────────────────────────────┘
 *      ↑
 *      border-l-4 accent quando milestone
 *
 * Mudanças vs versão anterior (PR-M1-02-bis):
 *   - **Badge acima do nome** (eyebrow style): nome ganha 100% da
 *     largura, não compete por espaço com badge milestone gigante.
 *   - **Border lateral accent** em milestones (consume `cardClassName`
 *     do helper de hierarquia visual — antes não estava sendo usado
 *     no card mobile, redundância visual perdida).
 *   - **Badge `compact`** ("ASSINADO" curto) com peso milestone preservado.
 *   - **`line-clamp-2`** no nome: nomes longos quebram em 2 linhas
 *     elegantemente em vez de truncar com "...".
 *   - **"Há X dias"** via `formatRelativeDate`: economiza largura e
 *     comunica freshness do funil (vs verbose "Criado em 25 de abr.
 *     de 2026").
 *
 * Tap target do card todo (Link envolvente). Foco visível via ring.
 */
export interface LeadsCardListProps {
  leads: Lead[];
}

export function LeadsCardList({ leads }: LeadsCardListProps) {
  return (
    <ul className="space-y-3 sm:hidden">
      {leads.map((lead) => {
        const style = getLeadStageStyle(lead.stage);
        return (
          <li key={lead.id}>
            <Link
              to={`/superadmin/leads/${lead.id}`}
              className={`flex flex-col gap-2 rounded-lg border border-border bg-card p-4 shadow-sm transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${style.cardClassName ?? ''}`}
            >
              {/* Badge no topo (eyebrow). Compact pra usar label curto
                  ("ASSINADO" não "CONTRATO ASSINADO") — peso visual de
                  milestone preservado pelo bg/border/uppercase do helper. */}
              <div>
                <LeadStageBadge stage={lead.stage} compact />
              </div>

              {/* Nome em destaque, ocupa largura toda. line-clamp-2
                  permite que nomes longos quebrem em 2 linhas
                  graciosamente sem truncar com "...". */}
              <p className="line-clamp-2 font-semibold text-foreground">{lead.full_name}</p>

              <div className="space-y-0.5 text-sm text-muted-foreground">
                {lead.clinic_name_guess ? (
                  <p className="line-clamp-1">{lead.clinic_name_guess}</p>
                ) : null}
                <p className="line-clamp-1 break-all">{lead.email}</p>
                <p>{formatRelativeDate(lead.created_at)}</p>
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
